import {
    ButtonBase,
    Checkbox,
    DialogContent,
    FormControlLabel,
    FormGroup,
    TextField,
    IconButton,
} from "@mui/material";
import _, { toInteger } from "lodash";
import React, { useContext, useEffect, useMemo, useState } from "react";
import NumPad from "react-numpad";
import { AppContext } from "../../../../AppContext";
import backIcon from "../../../../images/back-icon.svg";
import { ItemDialog, ItemSmallButton, useStyles } from "./style";
import { PosContext } from "../../../../shared/PosContext";
import { isRoleAdmin } from "../../../../frappeConfig";
import BundleConfigurator from "./BundleConfigurator";

const normalizeBundle = (raw) => {
    if (!raw) return null;
    const categories = Array.isArray(raw.categories) ? raw.categories : [];

    return {
        ...raw,
        categories: categories.map((cat) => {
            const allow_multiple_select =
                cat?.allow_multiple_select === undefined
                    ? false
                    : !!cat.allow_multiple_select;
            const mandatory =
                cat?.mandatory === undefined ? true : !!cat.mandatory;
            const options = Array.isArray(cat?.options) ? cat.options : [];

            return {
                ...cat,
                allow_multiple_select,
                mandatory,
                options: options.map((opt) => ({
                    ...opt,
                    default: opt?.default === undefined ? false : !!opt.default,
                    qty: opt?.qty === undefined ? 1 : opt.qty,
                })),
            };
        }),
    };
};

const pickDefaultsForCategory = (cat) => {
    const options = Array.isArray(cat?.options) ? cat.options : [];
    if (!options.length) return cat?.allow_multiple_select ? [] : null;

    const allowMulti = !!cat?.allow_multiple_select;
    const mandatory = cat?.mandatory === undefined ? true : !!cat.mandatory;

    if (allowMulti) {
        const defaults = options.filter((o) => !!o.default);
        if (defaults.length) return defaults;
        return mandatory ? [options[0]] : [];
    }

    // Single-select + optional: never auto-select anything (ignore default=true)
    if (!mandatory) return null;

    const defaults = options.filter((o) => !!o.default);
    if (defaults.length === 1) return defaults[0];
    if (defaults.length > 1) return defaults[0];
    return mandatory ? options[0] : null;
};

const extractSelectedOptionCodes = (selectedOptions) => {
    const codes = new Set();
    if (!selectedOptions) return codes;
    Object.values(selectedOptions).forEach((v) => {
        if (Array.isArray(v)) {
            v.forEach((o) => o?.item_code && codes.add(o.item_code));
        } else if (v?.item_code) {
            codes.add(v.item_code);
        }
    });
    return codes;
};

function AddItemWidget({
    item,
    onClose,
    open,
    onAddItem,
    onUpdateItem,
    pushItemWorker,
    editedOrderItem,
}) {
    const classes = useStyles();
    const [selectedAddons, setSelectedAddons] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState({});
    const [bundleStep, setBundleStep] = useState(0);

    const { userInfo, elevatedPrivileges } = useContext(AppContext);

    const { addonsItems } = useContext(PosContext);

    const addonItems = useMemo(() => {
        if (addonsItems) {
            return addonsItems;
        }
        return [];
    }, [addonsItems]);
    const menuBundle = useMemo(() => {
        try {
            if (item && item.menu_item_bundle) {
                const parsed = JSON.parse(item.menu_item_bundle);
                if (
                    parsed &&
                    Array.isArray(parsed.categories) &&
                    parsed.categories.length > 0
                ) {
                    return normalizeBundle(parsed);
                }
                if (Array.isArray(parsed) && parsed.length > 0) {
                    return normalizeBundle({ categories: parsed });
                }
            }
        } catch (e) {}
        return null;
    }, [item]);

    const hasExtraAddons = useMemo(() => {
        return Array.isArray(item?.allowed_addons) && item.allowed_addons.length > 0;
    }, [item]);
    const isNotesOnly = useMemo(() => {
        return !(menuBundle && menuBundle.categories) && !hasExtraAddons;
    }, [menuBundle, hasExtraAddons]);

    const [qty, setQty] = useState(1);
    const [notes, setNotes] = useState("");
    const [isEditingPrice, setIsEditingPrice] = useState(false);

    const { menuAddons } = useContext(AppContext);

    const [itemRate, setItemRate] = useState(0);
    const [total, setTotal] = useState(0);
    const [addonsTotal, setAddonsTotal] = useState(0);

    const reset = () => {
        setSelectedAddons([]);
        setSelectedOptions({});
        setQty(1);
        setNotes("");
        setItemRate(0);
        setTotal(0);
        setAddonsTotal(0);
        setIsEditingPrice(false);
    };



    useEffect(() => {
        if (editedOrderItem) {
            setItemRate(editedOrderItem.item.standard_rate);
            const tmpQty = editedOrderItem.qty;
            setNotes(editedOrderItem.notes);
            setQty(tmpQty);

            // Split addons into options vs extra addons based on menu bundle / allowed_addons
            const existingAddons = Array.isArray(editedOrderItem.addons)
                ? editedOrderItem.addons
                : [];

            const allowedAddons = item?.allowed_addons || [];

            // Build defaults
            const optionsMap = {};
            if (menuBundle && menuBundle.categories) {
                menuBundle.categories.forEach((cat) => {
                    const allowMulti = !!cat.allow_multiple_select;
                    const opts = Array.isArray(cat.options) ? cat.options : [];

                    if (!opts.length) return;

                    if (allowMulti) {
                        const selected = opts.filter((o) =>
                            existingAddons.some(
                                (ad) => ad.item_code === o.item_code
                            )
                        );
                        const computed =
                            selected.length > 0
                                ? selected
                                : pickDefaultsForCategory(cat);
                        optionsMap[cat.name] = Array.isArray(computed)
                            ? computed
                            : computed
                            ? [computed]
                            : [];
                    } else {
                        const found = existingAddons.find((ad) =>
                            opts.some((opt) => opt.item_code === ad.item_code)
                        );
                        if (found) {
                            const opt = opts.find(
                                (o) => o.item_code === found.item_code
                            );
                            optionsMap[cat.name] = opt || pickDefaultsForCategory(cat);
                        } else {
                            optionsMap[cat.name] = pickDefaultsForCategory(cat);
                        }
                    }
                });
            }

            setSelectedOptions(optionsMap);
            // Extra addons: those that are in allowed_addons but not part of options
            const optionCodes = extractSelectedOptionCodes(optionsMap);
            const extraAddons = existingAddons
                .map((i) => i.item_code)
                .filter((code) => allowedAddons.includes(code) && !optionCodes.has(code));
            setSelectedAddons(extraAddons);
        } else {
            if (item) {
                const { standard_rate, notes } = item;
                setItemRate(standard_rate);
                setNotes(notes);
                setQty(1);
                setTotal(standard_rate * 1);

                // Default select first option of each category
                if (menuBundle && menuBundle.categories) {
                    const defaults = {};
                    menuBundle.categories.forEach((cat) => {
                        defaults[cat.name] = pickDefaultsForCategory(cat);
                    });
                    setSelectedOptions(defaults);
                } else {
                    setSelectedOptions({});
                }
            } else {
                setItemRate(0);
                setQty(1);
                setNotes("");
                setSelectedOptions({});
            }
        }
        setBundleStep(0);
    }, [item, open, editedOrderItem, menuBundle]);

    useEffect(() => {
        const calculateAddonsTotal = () => {
            let computedAddonsTotal = 0;
            const base = itemRate * qty;

            // Extra addons (from allowed_addons) use system addon rates
            if (menuAddons && selectedAddons && selectedAddons.length > 0) {
                selectedAddons.forEach((oneItem) => {
                    const foundItem = _.find(menuAddons, {
                        item_code: oneItem,
                    });
                    if (foundItem) {
                        computedAddonsTotal +=
                            (foundItem.standard_rate || 0) * qty;
                    }
                });
            }

            // Options (from menu_item_bundle) use added_price
            if (selectedOptions && Object.keys(selectedOptions).length > 0) {
                Object.values(selectedOptions).forEach((optOrList) => {
                    const list = Array.isArray(optOrList)
                        ? optOrList
                        : optOrList
                        ? [optOrList]
                        : [];
                    list.forEach((opt) => {
                        const added = parseFloat(opt?.added_price) || 0;
                        const optQty = parseFloat(opt?.qty) || 1;
                        computedAddonsTotal += added * qty * optQty;
                    });
                });
            }

            setTotal(base + computedAddonsTotal);
            setAddonsTotal(computedAddonsTotal);
        };

        const timer = setTimeout(calculateAddonsTotal, 100);

        return () => {
            clearTimeout(timer);
        };
    }, [qty, selectedAddons, selectedOptions, itemRate, menuAddons]);

    const handleNotesChange = (event) => {
        setNotes(event.target.value);
    };

    const addQty = (addedQty) => {
        if (qty + addedQty > 0) setQty(qty + addedQty);
    };

    const handleClose = () => {
        reset();
        if (onClose) {
            onClose();
        }
    };

    const handleClick = () => {
        if (editedOrderItem) {
            const updatedEntry = {
                ...editedOrderItem,
                qty,
                addons: (() => {
                    const optionsAddons = Object.values(
                        selectedOptions || {}
                    )
                        .flatMap((optOrList) =>
                            Array.isArray(optOrList)
                                ? optOrList
                                : optOrList
                                ? [optOrList]
                                : []
                        )
                        .map((opt) => ({
                            item_code: opt.item_code,
                            standard_rate: Number(opt.added_price) || 0,
                            qty: opt?.qty || 1,
                        }));
                    return [...selectedAddons, ...optionsAddons];
                })(),
                notes,
            };

            if (updatedEntry.item) {
                updatedEntry.item.qty = qty;
                updatedEntry.item.addons = (() => {
                    const optionsAddons = Object.values(
                        selectedOptions || {}
                    )
                        .flatMap((optOrList) =>
                            Array.isArray(optOrList)
                                ? optOrList
                                : optOrList
                                ? [optOrList]
                                : []
                        )
                        .map((opt) => ({
                            item_code: opt.item_code,
                            standard_rate: Number(opt.added_price) || 0,
                            qty: opt?.qty || 1,
                        }));
                    return [...selectedAddons, ...optionsAddons];
                })();
                updatedEntry.item.notes = notes;
                // Update the rate if admin edited it
                if (isRoleAdmin(userInfo.role_profile_name)) {
                    updatedEntry.item.standard_rate = itemRate;
                }
            }
            onUpdateItem(updatedEntry, false);
        } else {
            // Create item with potentially edited rate for admin users
            const itemToAdd = {
                ...item,
                qty: qty,
                notes: notes,
                // Use edited rate if admin, otherwise use original rate
                standard_rate: isRoleAdmin(userInfo.role_profile_name)
                    ? itemRate
                    : item.standard_rate,
            };
            const optionsAddons = Object.values(selectedOptions || {})
                .flatMap((optOrList) =>
                    Array.isArray(optOrList)
                        ? optOrList
                        : optOrList
                        ? [optOrList]
                        : []
                )
                .map((opt) => ({
                    item_code: opt.item_code,
                    standard_rate: Number(opt.added_price) || 0,
                    qty: opt?.qty || 1,
                }));
            const combinedAddons = [...selectedAddons, ...optionsAddons];
            onAddItem(itemToAdd, qty, combinedAddons, notes, true);
        }
        handleClose();
        // setTimeout(reset, 1000);
    };

    const handleCheckboxChange = (event) => {
        const { checked, value } = event.target;

        setSelectedAddons((prev) => {
            if (checked) {
                return _.xor(prev, [value]);
            } else {
                return _.without(selectedAddons, value);
            }
        });
    };

    const handlePriceChange = (event) => {
        const value = event.target.value;
        // Allow empty string during editing
        if (value === "") {
            setItemRate("");
        } else {
            const newPrice = parseFloat(value);
            if (!isNaN(newPrice) && newPrice >= 0) {
                setItemRate(newPrice);
            }
        }
    };

    const handlePriceFocus = (event) => {
        // Select all text when field is focused
        event.target.select();
    };

    const handlePriceEditStart = () => {
        setIsEditingPrice(true);
    };

    const handlePriceEditSave = () => {
        // If field is empty or invalid, revert to original value
        if (itemRate === "" || itemRate < 0 || isNaN(itemRate)) {
            const originalRate = editedOrderItem
                ? editedOrderItem.item.standard_rate
                : item.standard_rate;
            setItemRate(originalRate);
        }
        setIsEditingPrice(false);
    };

    const handlePriceEditCancel = () => {
        // Revert to original value
        const originalRate = editedOrderItem
            ? editedOrderItem.item.standard_rate
            : item.standard_rate;
        setItemRate(originalRate);
        setIsEditingPrice(false);
    };

    const handlePriceKeyPress = (event) => {
        if (event.key === "Enter") {
            handlePriceEditSave();
        } else if (event.key === "Escape") {
            handlePriceEditCancel();
        }
    };

    return item ? (
        <>
            <ItemDialog
                className={classes.DialogWrap}
                onClose={() => {
                    handleClose();
                }}
                open={open}
            >
                <DialogContent className={classes.ContentWrapper}>
                    <div>
                        <button className="BackBtn">
                            <img
                                src={backIcon}
                                onClick={() => {
                                    handleClose();
                                }}
                                alt="Back"
                            />
                        </button>
                    </div>

                    <div className={classes.Header}>
                        <div className={classes.ItemDesc}>
                            <img
                                className={classes.ItemDescImage}
                                src={item.cover}
                                alt={item.item_code}
                            />
                            <div>
                                <div className={classes.ItemDescName}>
                                    {item.item_code}
                                </div>
                                {isRoleAdmin(userInfo.role_profile_name) ? (
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                        }}
                                    >
                                        {isEditingPrice ? (
                                            <TextField
                                                type="number"
                                                value={itemRate}
                                                onChange={handlePriceChange}
                                                onFocus={handlePriceFocus}
                                                onKeyDown={handlePriceKeyPress}
                                                onBlur={handlePriceEditSave}
                                                variant="outlined"
                                                size="small"
                                                autoFocus
                                                inputProps={{
                                                    min: 0,
                                                    step: 0.01,
                                                    style: { fontSize: "14px" },
                                                }}
                                                sx={{
                                                    "& .MuiOutlinedInput-root":
                                                        {
                                                            height: "32px",
                                                            "& fieldset": {
                                                                borderColor:
                                                                    "#ddd",
                                                            },
                                                            "&:hover fieldset":
                                                                {
                                                                    borderColor:
                                                                        "#999",
                                                                },
                                                            "&.Mui-focused fieldset":
                                                                {
                                                                    borderColor:
                                                                        "#1976d2",
                                                                },
                                                        },
                                                }}
                                            />
                                        ) : (
                                            <>
                                                <div
                                                    className={
                                                        classes.ItemDescPrice
                                                    }
                                                >
                                                    {itemRate || 0}
                                                </div>
                                                {elevatedPrivileges ||
                                                isRoleAdmin(
                                                    userInfo.role_profile_name
                                                ) ? (
                                                    <IconButton
                                                        size="small"
                                                        onClick={
                                                            handlePriceEditStart
                                                        }
                                                        sx={{
                                                            padding: "4px",
                                                            "&:hover": {
                                                                backgroundColor:
                                                                    "rgba(0, 0, 0, 0.04)",
                                                            },
                                                        }}
                                                    >
                                                        ✏️
                                                    </IconButton>
                                                ) : (
                                                    <></>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className={classes.ItemDescPrice}>
                                        {item.standard_rate}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={classes.ItemQty}>
                            <ItemSmallButton
                                onClick={() => addQty(-1)}
                                className={classes.ItemQtyMinus}
                            >
                                -
                            </ItemSmallButton>
                            <NumPad.Number
                                onChange={(value) => {
                                    setQty(value > 0 ? toInteger(value) : 1);
                                }}
                                isDecimal={false}
                                position={"startBottomRight"}
                                min={1}
                                value={0}
                            >
                                <div className={classes.ItemQtyText}>{qty}</div>
                            </NumPad.Number>
                            <ItemSmallButton
                                onClick={() => addQty(1)}
                                className={classes.ItemQtyPlus}
                            >
                                +
                            </ItemSmallButton>
                        </div>
                    </div>

                    <div className={classes.ScrollableContent}>
                        {menuBundle && menuBundle.categories ? (
                            <BundleConfigurator
                                item={item}
                                menuBundle={menuBundle}
                                addonItems={addonItems}
                                menuAddons={menuAddons}
                                selectedOptions={selectedOptions}
                                setSelectedOptions={setSelectedOptions}
                                selectedAddons={selectedAddons}
                                setSelectedAddons={setSelectedAddons}
                                notes={notes}
                                setNotes={setNotes}
                                bundleStep={bundleStep}
                                setBundleStep={setBundleStep}
                                classes={classes}
                                qty={qty}
                            />
                        ) : (
                            <>
                                 {hasExtraAddons ? (
                                     <div className={classes.Addons}>
                                         <div>Extra Add-ons</div>
                                         <FormGroup
                                             className={classes.AddonsList}
                                         >
                                             {item.allowed_addons
                                                 ? item.allowed_addons.map(
                                                       (addonsItem) => {
                                                           const addon =
                                                               addonItems.find(
                                                                   (i) =>
                                                                       i.item_code ===
                                                                       addonsItem
                                                               ) || {};
                                                           return (
                                                               <div
                                                                   className={
                                                                       classes.AddonsRaw
                                                                   }
                                                                   key={addonsItem}
                                                               >
                                                                   <FormControlLabel
                                                                       control={
                                                                           <Checkbox
                                                                               onChange={
                                                                                   handleCheckboxChange
                                                                               }
                                                                               checked={
                                                                                   selectedAddons.find(
                                                                                       (i) =>
                                                                                           i ===
                                                                                           addonsItem
                                                                                   ) !=
                                                                                   null
                                                                               }
                                                                               value={
                                                                                   addonsItem
                                                                               }
                                                                           />
                                                                       }
                                                                       label={`${addonsItem} `}
                                                                   />
                                                                   <span>
                                                                       {addon.standard_rate}
                                                                   </span>
                                                               </div>
                                                           );
                                                       }
                                                   )
                                                 : ""}
                                         </FormGroup>
                                     </div>
                                 ) : null}
                                <div className={classes.Notes}>
                                    <div>Notes</div>
                                    <textarea
                                        value={notes}
                                        onChange={handleNotesChange}
                                        placeholder="Add notes here"
                                         className={
                                             isNotesOnly
                                                 ? `${classes.NotesArea} ${classes.NotesAreaLarge}`
                                                 : classes.NotesArea
                                         }
                                    ></textarea>
                                </div>
                            </>
                        )}

                    </div>

                    <div className={classes.Footer}>
                        <div>
                            <div className={classes.TotalText}>Total</div>
                            <div>
                                <div className={classes.TotalPrice}>
                                    {total}
                                </div>
                                <div className={classes.AddonsPrice}>
                                    (Add-ons {addonsTotal})
                                </div>
                            </div>
                        </div>
                        <ButtonBase
                            onClick={handleClick}
                            className={classes.DialogAction}
                        >
                            {pushItemWorker.isLoading ? "Saving ..." : "Save"}
                        </ButtonBase>
                    </div>
                </DialogContent>
            </ItemDialog>
        </>
    ) : (
        ""
    );
}

export default AddItemWidget;
